import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';
import { getSpecies, type Species } from '../../api/species';
import SpeciesCombobox from '../SpeciesCombobox';
import type { CreateBirdDto } from '../../api/birds';
import {
  mapCertificateToCreateBirdDraft,
  matchSpeciesId,
  parseTransferPackage,
  type BirdCertificateTransferPackage,
} from '../../utils/birdCertificateShare';

interface ImportBirdCertificateModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (draft: CreateBirdDto) => Promise<void> | void;
}

export default function ImportBirdCertificateModal({
  open,
  onClose,
  onImport,
}: ImportBirdCertificateModalProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [pkg, setPkg] = useState<BirdCertificateTransferPackage | null>(null);
  const [fileName, setFileName] = useState('');
  const [species, setSpecies] = useState<Species[]>([]);
  const [speciesId, setSpeciesId] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!open) return;
    getSpecies()
      .then(setSpecies)
      .catch(() => toast.error('Failed to load species catalog'));
  }, [open]);

  const suggestedSpeciesId = useMemo(() => {
    if (!pkg) return null;
    return matchSpeciesId(species, pkg.certificate);
  }, [pkg, species]);

  const selectedSpecies = useMemo(
    () => species.find((s) => s.id === speciesId) ?? null,
    [species, speciesId],
  );

  useEffect(() => {
    if (suggestedSpeciesId) setSpeciesId(suggestedSpeciesId);
  }, [suggestedSpeciesId]);

  const reset = () => {
    setPkg(null);
    setFileName('');
    setSpeciesId('');
    setDragActive(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const loadFile = async (file: File) => {
    try {
      const text = await file.text();
      const parsed = parseTransferPackage(JSON.parse(text));
      setPkg(parsed);
      setFileName(file.name);
      toast.success('Certificate transfer file loaded');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Invalid transfer file');
      reset();
    }
  };

  const handleFile = async (file: File | null) => {
    if (!file) return;
    await loadFile(file);
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const handleImport = async () => {
    if (!pkg) {
      toast.error('Choose a transfer file first');
      return;
    }
    if (!speciesId) {
      toast.error('Select a matching species in this instance');
      return;
    }
    setSaving(true);
    try {
      const draft = mapCertificateToCreateBirdDraft(pkg.certificate, speciesId);
      await onImport(draft);
      toast.success('Bird draft imported — review and save');
      reset();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Failed to import certificate');
    } finally {
      setSaving(false);
    }
  };

  const bird = pkg?.certificate.bird;

  return (
    <BrandModal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      maxWidthClass="max-w-2xl"
      panelClassName="overflow-visible"
    >
      <BrandModalHeader
        title="Import bird certificate"
        subtitle="Load a seller's BirdMan transfer file (.birdman.json) to prefill Add Bird."
        onClose={() => {
          reset();
          onClose();
        }}
      />
      <BrandModalBody className="space-y-6 overflow-visible pb-6 sm:pb-8">
        <section className="space-y-2">
          <label className="block text-sm font-semibold text-slate-800">1. Transfer file</label>
          <input
            ref={fileRef}
            type="file"
            accept=".json,.birdman.json,application/json"
            className="sr-only"
            onChange={(e) => void handleFile(e.target.files?.[0] || null)}
          />
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click();
            }}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            className={[
              'flex min-h-[8.5rem] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors sm:min-h-[9.5rem]',
              dragActive
                ? 'border-cyan-500 bg-cyan-50/80'
                : 'border-slate-300 bg-slate-50/60 hover:border-cyan-400 hover:bg-cyan-50/40',
            ].join(' ')}
          >
            <ArrowUpTrayIcon className="h-8 w-8 text-cyan-600 sm:h-9 sm:w-9" aria-hidden />
            <p className="mt-3 text-sm font-medium text-slate-800">
              {fileName || 'Drop .birdman.json here or click to browse'}
            </p>
            <p className="mt-1 max-w-md text-xs text-slate-500">
              Export this file from Reports → Bird certificate → Export transfer file
            </p>
          </div>
          {fileName ? (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <span className="flex min-w-0 items-center gap-2 text-slate-700">
                <DocumentTextIcon className="h-4 w-4 shrink-0 text-cyan-600" aria-hidden />
                <span className="truncate">{fileName}</span>
              </span>
              <button
                type="button"
                className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                aria-label="Remove file"
                onClick={(e) => {
                  e.stopPropagation();
                  reset();
                }}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ) : null}
        </section>

        {bird ? (
          <section className="rounded-xl border border-slate-200 bg-white/80 p-4 sm:p-5">
            <h4 className="text-sm font-semibold text-slate-800">Certificate preview</h4>
            <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 sm:gap-x-6 sm:gap-y-3">
              <div className="min-w-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Band</dt>
                <dd className="mt-0.5 truncate font-mono font-medium text-slate-900">
                  {bird.identification?.bandId || bird.bandNumber}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Certificate</dt>
                <dd className="mt-0.5 truncate font-medium text-slate-900">{pkg?.certificate.certificateNumber}</dd>
              </div>
              <div className="min-w-0 sm:col-span-2">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Species (from file)</dt>
                <dd className="mt-0.5 text-slate-900">
                  {bird.species?.commonName || 'Unknown'}
                  {bird.species?.scientificName ? (
                    <span className="text-slate-600 italic"> — {bird.species.scientificName}</span>
                  ) : null}
                </dd>
              </div>
              {pkg?.sourceInstance ? (
                <div className="min-w-0 sm:col-span-2">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Source instance</dt>
                  <dd className="mt-0.5 truncate text-slate-600">{pkg.sourceInstance}</dd>
                </div>
              ) : null}
            </dl>
          </section>
        ) : null}

        <section className="space-y-2">
          <label htmlFor="importSpeciesId" className="block text-sm font-semibold text-slate-800">
            2. Species in this instance
          </label>
          <p className="text-xs text-slate-500">
            Search by common or scientific name. Each word narrows the list.
          </p>
          <SpeciesCombobox
            id="importSpeciesId"
            species={species}
            value={speciesId}
            onChange={setSpeciesId}
            placeholder="Search species…"
            wrapperClassName=""
            optionsMaxHeightClass="max-h-48 sm:max-h-72"
          />
          {bird && suggestedSpeciesId && speciesId === suggestedSpeciesId ? (
            <p className="flex items-start gap-1.5 text-xs text-emerald-700">
              <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                Matched automatically to{' '}
                <span className="font-medium">{selectedSpecies?.commonName ?? 'this species'}</span>.
                Review before saving.
              </span>
            </p>
          ) : null}
          {bird && !suggestedSpeciesId ? (
            <p className="flex items-start gap-1.5 text-xs text-amber-700">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                No automatic species match — pick the closest species, then review genetics and lineage notes.
              </span>
            </p>
          ) : null}
        </section>
      </BrandModalBody>
      <BrandModalFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          className="btn btn-secondary w-full text-sm sm:w-auto"
          onClick={() => {
            reset();
            onClose();
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary w-full text-sm sm:w-auto"
          disabled={!pkg || !speciesId || saving}
          onClick={() => void handleImport()}
        >
          {saving ? 'Importing…' : 'Import into Add Bird'}
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
}
